import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type User } from "../api";

export function UsersPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<User | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => apiFetch<{ users: User[] }>("/api/admin/users"),
  });

  const createMutation = useMutation({
    mutationFn: (body: { email: string; password: string; display_name?: string; plan?: string; theme?: string; is_admin?: boolean }) =>
      apiFetch<{ user: User }>("/api/admin/users", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setCreating(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: Partial<User> & { id: string; password?: string }) =>
      apiFetch<{ user: User }>(`/api/admin/users/${id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/admin/users/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setDeleteConfirm(null);
    },
  });

  const users = data?.users ?? [];

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Users</h1>
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
          Add user
        </button>
      </header>

      <div className="admin-page-content">
        {isLoading ? (
          <p className="admin-muted">Loading users…</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Display name</th>
                  <th>Plan</th>
                  <th>Theme</th>
                  <th>Admin</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td>{u.display_name || "—"}</td>
                    <td>{u.plan}</td>
                    <td>{u.theme}</td>
                    <td>{u.is_admin ? "Yes" : "No"}</td>
                    <td>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="admin-actions">
                      <button type="button" className="btn btn-small" onClick={() => setEditing(u)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-small btn-danger"
                        onClick={() => setDeleteConfirm(u.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating && (
        <UserFormModal
          title="Add user"
          initial={{}}
          error={(createMutation.error as Error)?.message}
          loading={createMutation.isPending}
          onClose={() => setCreating(false)}
          onSubmit={(body) => createMutation.mutate(body as Parameters<typeof createMutation.mutate>[0])}
        />
      )}

      {editing && (
        <UserFormModal
          title="Edit user"
          initial={editing}
          error={(updateMutation.error as Error)?.message}
          loading={updateMutation.isPending}
          onClose={() => setEditing(null)}
          onSubmit={(body) =>
            updateMutation.mutate({
              id: editing.id,
              display_name: body.display_name as string,
              plan: body.plan as string,
              theme: body.theme as string,
              is_admin: body.is_admin as boolean,
              ...(body.password ? { password: body.password as string } : {}),
            })
          }
          allowPasswordChange
        />
      )}

      {deleteConfirm && (
        <div className="modal-backdrop" onClick={() => setDeleteConfirm(null)}>
          <div className="modal admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete user?</h3>
            <p className="admin-muted">This cannot be undone.</p>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(deleteConfirm)}
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface UserFormModalProps {
  title: string;
  initial: Partial<User>;
  error: string | undefined;
  loading: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
  allowPasswordChange?: boolean;
}

function UserFormModal({
  title,
  initial,
  error,
  loading,
  onClose,
  onSubmit,
  allowPasswordChange = false,
}: UserFormModalProps) {
  const [email, setEmail] = useState(initial.email ?? "");
  const [password, setPassword] = useState("");
  const [display_name, setDisplayName] = useState(initial.display_name ?? "");
  const [plan, setPlan] = useState(initial.plan ?? "free");
  const [theme, setTheme] = useState(initial.theme ?? "dark");
  const [is_admin, setIsAdmin] = useState(initial.is_admin ?? false);

  const isEdit = !!initial.id;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const body: Record<string, unknown> = {
      email,
      display_name: display_name.trim(),
      plan: plan.trim() || "free",
      theme: theme.trim() || "dark",
      is_admin,
    };
    if (!isEdit) {
      if (!password || password.length < 6) return;
      body.password = password;
    } else if (allowPasswordChange && password) {
      body.password = password;
    }
    onSubmit(body);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal admin-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <form onSubmit={handleSubmit}>
          {error && <div className="auth-error">{error}</div>}
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isEdit}
            />
          </label>
          {!isEdit && (
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                required
                minLength={6}
              />
            </label>
          )}
          {allowPasswordChange && isEdit && (
            <label>
              New password (leave blank to keep)
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                minLength={6}
              />
            </label>
          )}
          <label>
            Display name
            <input type="text" value={display_name} onChange={(e) => setDisplayName(e.target.value)} />
          </label>
          <label>
            Plan
            <select value={plan} onChange={(e) => setPlan(e.target.value)}>
              <option value="free">free</option>
              <option value="pro">pro</option>
            </select>
          </label>
          <label>
            Theme
            <select value={theme} onChange={(e) => setTheme(e.target.value)}>
              <option value="dark">dark</option>
              <option value="light">light</option>
            </select>
          </label>
          <label className="admin-checkbox">
            <input type="checkbox" checked={is_admin} onChange={(e) => setIsAdmin(e.target.checked)} />
            Admin
          </label>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving…" : isEdit ? "Save" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
